// src/pages/admin/Customers.tsx
//
// Admin: customer directory backed by the real imported FileMaker
// ledger (273 customers, ~2 431 invoices, ~5.6 M EUR HTVA) — replaces
// the prior local-storage seed of 3 demo customers.
//
// M25 customer model: each row carries the FileMaker fields the
// directory needs plus per-customer aggregates (total_invoices,
// total_spent_htva) computed server-side. Search hits name / city /
// VAT number (server). Click a card to open the recent-invoices panel
// (drilldown via /api/sales/invoices?customer_id=) and to edit the
// customer-level metadata we don't get from FileMaker (pricing tier,
// monthly cap, contact name). The shape is the same as
// CustomerAccount for downstream compatibility.

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MapPin,
  Mail,
  Plus,
  Search,
  Building2,
  Receipt,
  ChevronRight,
  X,
  EyeOff,
  Eye,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { EmptyState } from "@/components/ui/EmptyState";
import { cn } from "@/lib/cn";
import { formatPrice } from "@/lib/formatPrice";
import { authHeaders, hideEntities, unhideEntities } from "@/lib/api";
import { useAuth } from "@/components/auth/AuthContext";
import { type CustomerAccount, type PricingTier } from "@/lib/demoData";
import { InvoiceDetailPanel } from "@/components/feature/InvoiceDetailPanel";

const TIER_VARIANT: Record<PricingTier, "default" | "teal" | "success" | "warning"> = {
  standard: "default",
  preferred: "teal",
  vip: "success",
  quote: "warning",
};

// Display copy lives in the locale catalogue; the tier VALUE stays the
// machine value used by the API.
const TIER_LABEL_KEY: Record<PricingTier, string> = {
  standard: "admin.customers.standard",
  preferred: "admin.customers.preferredDiscount",
  vip: "admin.customers.vipDiscount",
  quote: "admin.customers.quoteOnly",
};

// Per-customer overrides stored in localStorage; merged over the
// FileMaker-driven defaults so the admin can adjust pricing tier and
// contact without re-running the import.
const OVERRIDES_KEY = "bench-customer-overrides-v1";
type Overrides = Record<string, { pricingTier?: PricingTier; monthlyLimitEUR?: number; contactName?: string }>;

function readOverrides(): Overrides {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(OVERRIDES_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Overrides;
  } catch {
    return {};
  }
}

function writeOverrides(o: Overrides) {
  try {
    window.localStorage.setItem(OVERRIDES_KEY, JSON.stringify(o));
  } catch {
    // localStorage disabled — the in-memory copy in state still works
  }
}

interface ApiCustomer {
  customer_id: number;
  name: string;
  vat_number: string | null;
  vat_regime: string | null;
  country: string | null;
  street: string | null;
  street_full: string | null;
  city: string | null;
  zip_city: string | null;
  email: string | null;
  phone: string | null;
  status: string | null;
  created_date: string | null;
  total_invoices: number;
  total_spent_htva: number;
  /** True when this customer has been hidden by an admin via the
   * catalog-visibility feature. Set by /api/customers when
   * include_hidden=true. */
  hidden?: boolean;
}

interface ApiInvoice {
  invoice_number: string;
  invoice_date: string | null;
  customer_id: number | null;
  customer_name_snapshot: string | null;
  total_htva: number | string | null;
  total_tvac: number | string | null;
}

/** Map the real FileMaker row + admin overrides onto the
 * CustomerAccount shape the rest of the app uses (cart, portal, etc.). */
export function toCustomerAccount(c: ApiCustomer, overrides: Overrides[string] = {}): CustomerAccount {
  const o = overrides;
  return {
    id: String(c.customer_id),
    companyName: c.name,
    contactName: o.contactName ?? "",
    email: c.email ?? "",
    country: c.country ?? "",
    pricingTier: o.pricingTier ?? "standard",
    monthlyLimitEUR: o.monthlyLimitEUR ?? 0,
    shipTo: [c.street_full, c.zip_city, c.country].filter(
      (x): x is string => Boolean(x),
    ),
    activeSince: c.created_date ?? "",
  };
}

type Filter = "all" | "vip" | "preferred" | "standard" | "quote";
type VisibilityFilter = "visible" | "hidden" | "all";

export function AdminCustomers() {
  const auth = useAuth();
  const { t } = useTranslation();
  const [apiRows, setApiRows] = useState<ApiCustomer[]>([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  // Customer visibility: show only visible by default. The "Show
  // hidden" toggle in the toolbar flips to include_hidden=true and
  // populates the per-row hidden chip.
  const [visibility, setVisibility] = useState<VisibilityFilter>("visible");
  const [showCreate, setShowCreate] = useState(false);
  const [overrides, setOverrides] = useState<Overrides>(readOverrides);
  const [openInvoiceFor, setOpenInvoiceFor] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Bulk-action state. We track which customer ids the admin
  // toggled locally so the row label flips without a full reload.
  const [busyCustomerIds, setBusyCustomerIds] = useState<Set<number>>(
    new Set(),
  );
  const [bulkBusy, setBulkBusy] = useState<"hide" | "unhide" | null>(null);
  const [bulkError, setBulkError] = useState("");

  // Debounced search + initial load.
  useEffect(() => {
    if (!auth.isReady || !auth.isAuthenticated) return;
    setLoading(true);
    setError("");
    const handle = setTimeout(() => {
      const url = new URL("/api/customers", window.location.origin);
      if (query) url.searchParams.set("q", query);
      // include_hidden=true whenever the user wants to see hidden
      // rows (either "hidden" or "all"); the server populates the
      // per-row hidden flag in that case. The default ("visible")
      // leaves the param off so hidden customers are excluded.
      if (visibility !== "visible") {
        url.searchParams.set("include_hidden", "true");
      }
      url.searchParams.set("limit", "200");
      fetch(url.toString().replace(window.location.origin, ""), {
        headers: authHeaders(),
      })
        .then(async (r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const body = (await r.json()) as { rows: ApiCustomer[]; total: number };
          setApiRows(body.rows);
          setTotal(body.total);
        })
        .catch((e) => setError(String(e instanceof Error ? e.message : e)))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(handle);
  }, [auth.isReady, auth.isAuthenticated, query, visibility]);

  const customers: CustomerAccount[] = useMemo(
    () => apiRows.map((c) => toCustomerAccount(c, overrides[c.customer_id])),
    [apiRows, overrides],
  );

  const visible = useMemo(() => {
    if (filter === "all") return customers;
    return customers.filter((c) => c.pricingTier === filter);
  }, [customers, filter]);

  // Post-fetch visibility filter: when the admin picks "Visible only"
  // we already asked the server to exclude hidden. "Hidden only" /
  // "All" let them audit hidden rows in place.
  const displayRows = useMemo(() => {
    return visible.filter((c) => {
      const api = apiRows.find((r) => r.customer_id === Number(c.id));
      const isHidden = api?.hidden === true;
      if (visibility === "visible") return !isHidden;
      if (visibility === "hidden") return isHidden;
      return true;
    });
  }, [visible, apiRows, visibility]);

  // ----- admin: hide / show a single customer -----
  async function toggleCustomerVisibility(c: ApiCustomer) {
    const cid = c.customer_id;
    const wasHidden = c.hidden === true;
    setBusyCustomerIds((s) => new Set(s).add(cid));
    try {
      if (wasHidden) {
        await unhideEntities({
          entity_type: "customer", scope: "selected", ids: [cid],
        });
      } else {
        await hideEntities({
          entity_type: "customer", scope: "selected", ids: [cid],
        });
      }
      // Optimistic local flip so the card label changes immediately.
      setApiRows((rows) =>
        rows.map((r) =>
          r.customer_id === cid ? { ...r, hidden: !wasHidden } : r,
        ),
      );
    } catch (e) {
      setBulkError(String(e instanceof Error ? e.message : e));
    } finally {
      setBusyCustomerIds((s) => {
        const next = new Set(s);
        next.delete(cid);
        return next;
      });
    }
  }

  // ----- admin: bulk hide/unhide matching the current query -----
  async function bulkVisibility(action: "hide" | "unhide") {
    setBulkBusy(action);
    setBulkError("");
    try {
      await (action === "hide"
        ? hideEntities({
            entity_type: "customer", scope: "filter",
            filter: { q: query || undefined },
          })
        : unhideEntities({
            entity_type: "customer", scope: "filter",
            filter: { q: query || undefined },
          }));
      // Force a re-fetch so the totals + chips refresh.
      setVisibility((v) => v);
      setQuery((q) => q); // no-op but triggers effect via dep tracking
      // Manually re-trigger by toggling query briefly
      const q = query;
      setQuery("");
      setTimeout(() => setQuery(q), 0);
    } catch (e) {
      setBulkError(String(e instanceof Error ? e.message : e));
    } finally {
      setBulkBusy(null);
    }
  }

  const hasActiveFilters = !!query;
  const bulkActionsEnabled = hasActiveFilters;

  function setOverride(customerId: number, patch: Overrides[string]) {
    const next = { ...overrides, [customerId]: { ...overrides[customerId], ...patch } };
    setOverrides(next);
    writeOverrides(next);
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-510 tracking-tight text-fg-primary">
            {t("admin.customers.title")}
          </h1>
          <p className="mt-1 text-sm text-fg-tertiary">
            {total}{" "}
            {t(
              total === 1
                ? "admin.customers.activeCustomerOne"
                : "admin.customers.activeCustomerOther",
            )}
            {" · "}
            {visible.length}{" "}
            {t("admin.customers.shown")}
            {query ? ` · ${t("admin.customers.searchFor")} "${query}"` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => setShowCreate((s) => !s)}>
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            {showCreate ? t("admin.customers.cancel") : t("admin.customers.newCustomer")}
          </Button>
        </div>
      </header>

      {/* Toolbar */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="relative">
            <Search
              className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-tertiary"
              strokeWidth={1.5}
            />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("admin.customers.searchPlaceholder")}
              className="pl-8"
              aria-label={t("admin.customers.searchAria")}
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="text-fg-quaternary">{t("admin.customers.filterLabel")}</span>
            {(
              [
                { v: "all", l: t("admin.customers.all") },
                { v: "vip", l: t("admin.customers.vip") },
                { v: "preferred", l: t("admin.customers.preferred") },
                { v: "standard", l: t("admin.customers.standard") },
                { v: "quote", l: t("admin.customers.quote") },
              ] as Array<{ v: Filter; l: string }>
            ).map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setFilter(opt.v)}
                aria-pressed={filter === opt.v}
                className={cn(
                  "rounded px-2 py-1 font-510 uppercase tracking-wider transition-colors",
                  filter === opt.v
                    ? "bg-benchmark-teal/20 text-benchmark-teal-bright"
                    : "bg-bg-elevated text-fg-secondary hover:bg-bg-hover hover:text-fg-primary",
                )}
              >
                {opt.l}
              </button>
            ))}
          </div>
          {/* Visibility filter: visible-only (default), hidden only,
              or all. Switching modes re-asks the server with
              include_hidden=true and re-renders the per-row chips. */}
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="text-fg-quaternary">{t("admin.customers.showLabel")}</span>
            {(
              [
                { v: "visible", l: t("admin.customers.visibleOnly") },
                { v: "hidden", l: t("admin.customers.hiddenOnly") },
                { v: "all", l: t("admin.customers.all") },
              ] as Array<{ v: VisibilityFilter; l: string }>
            ).map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setVisibility(opt.v)}
                aria-pressed={visibility === opt.v}
                className={cn(
                  "rounded px-2 py-1 font-510 uppercase tracking-wider transition-colors",
                  visibility === opt.v
                    ? "bg-benchmark-teal/20 text-benchmark-teal-bright"
                    : "bg-bg-elevated text-fg-secondary hover:bg-bg-hover hover:text-fg-primary",
                )}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {bulkActionsEnabled && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-benchmark-teal/40 bg-benchmark-teal/5 p-3 text-xs">
          <span className="font-510 uppercase tracking-wider text-benchmark-teal-bright">
            {t("admin.customers.bulkVisibility")}
          </span>
          <span className="text-fg-tertiary">
            {t("admin.customers.matchCount", { count: apiRows.length })}
          </span>
          <div className="ml-auto flex flex-wrap items-center gap-1.5">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => bulkVisibility("hide")}
              disabled={bulkBusy !== null}
              aria-label={t("admin.customers.hideAllAria")}
            >
              <EyeOff className="h-3 w-3" strokeWidth={1.5} />
              {bulkBusy === "hide"
                ? t("admin.customers.hiding")
                : t("admin.customers.hideCustomers", { count: apiRows.length })}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => bulkVisibility("unhide")}
              disabled={bulkBusy !== null}
              aria-label={t("admin.customers.unhideAllAria")}
            >
              <Eye className="h-3 w-3" strokeWidth={1.5} />
              {bulkBusy === "unhide"
                ? t("admin.customers.unhiding")
                : t("admin.customers.unhideCustomers", { count: apiRows.length })}
            </Button>
          </div>
          {bulkError && (
            <p className="basis-full text-[11px] text-accent-rose">{bulkError}</p>
          )}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-3 text-sm text-accent-rose">
          {t("admin.customers.unavailable")}
          ({error})
        </div>
      )}

      {loading && customers.length === 0 ? (
        <p className="py-8 text-center text-sm text-fg-tertiary">
          {t("admin.customers.loadingCustomers")}
        </p>
      ) : customers.length === 0 ? (
        <EmptyState
          icon={
            <Building2
              className="h-5 w-5 text-accent-indigo"
              strokeWidth={1.5}
            />
          }
          title={t("admin.customers.emptyLedgerTitle")}
          description={t("admin.customers.emptyLedgerDescription")}
        />
      ) : displayRows.length === 0 ? (
        <EmptyState
          title={t("admin.customers.emptyFilterTitle")}
          description={t("admin.customers.emptyFilterDescription")}
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {displayRows.map((c) => {
            const api = apiRows.find((r) => r.customer_id === Number(c.id))!;
            const isHidden = api.hidden === true;
            return (
              <Card
                key={c.id}
                className={cn(
                  "group overflow-hidden transition-colors",
                  isHidden
                    ? "border-accent-rose/40 hover:border-accent-rose/60"
                    : "hover:border-benchmark-teal/40",
                )}
              >
                <CardContent className="space-y-3 p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-510 text-fg-primary">
                        {c.companyName}
                      </h3>
                      <div className="mt-0.5 truncate text-xs text-fg-tertiary">
                        {c.country || "—"} ·{" "}
                        <span className="font-mono">#{c.id}</span>
                        {api.vat_number && (
                          <> · <span className="font-mono">{api.vat_number}</span></>
                        )}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <Badge
                        variant={TIER_VARIANT[c.pricingTier]}
                        size="sm"
                      >
                        {t(TIER_LABEL_KEY[c.pricingTier])}
                      </Badge>
                      {isHidden && (
                        <Badge variant="danger" size="sm">
                          {t("admin.customers.hidden")}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Metrics — real numbers from the imported ledger */}
                  <div className="grid grid-cols-3 gap-2 border-t border-border pt-3 text-xs">
                    <Metric
                      label={t("admin.customers.invoices")}
                      value={api.total_invoices.toLocaleString()}
                    />
                    <Metric
                      label={t("admin.customers.lifetimeHtva")}
                      value={formatPrice(api.total_spent_htva, "EUR")}
                    />
                    <Metric
                      label={t("admin.customers.country")}
                      value={c.country || "—"}
                    />
                  </div>

                  {/* Contact */}
                  <div className="space-y-1 border-t border-border pt-2 text-xs text-fg-secondary">
                    {c.email && (
                      <div className="flex items-center gap-1.5">
                        <Mail
                          className="h-3 w-3 text-benchmark-teal-bright"
                          strokeWidth={1.5}
                        />
                        <a
                          href={`mailto:${c.email}`}
                          className="truncate hover:text-fg-primary"
                        >
                          {c.email}
                        </a>
                      </div>
                    )}
                    {c.shipTo.length > 0 && (
                      <div className="flex items-start gap-1.5">
                        <MapPin
                          className="mt-0.5 h-3 w-3 text-benchmark-teal-bright"
                          strokeWidth={1.5}
                        />
                        <span className="line-clamp-2 text-fg-secondary">
                          {c.shipTo.join(", ")}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Tier override */}
                  <div className="flex items-center gap-1 border-t border-border pt-2 text-[10px] text-fg-quaternary">
                    <span>{t("admin.customers.tierLabel")}</span>
                    {(["standard", "preferred", "vip", "quote"] as PricingTier[]).map(
                      (tier) => (
                        <button
                          key={tier}
                          type="button"
                          onClick={() =>
                            setOverride(Number(c.id), { pricingTier: tier })
                          }
                          className={cn(
                            "rounded px-1.5 py-0.5 uppercase tracking-wider transition-colors",
                            c.pricingTier === tier
                              ? "bg-benchmark-teal/20 text-benchmark-teal-bright"
                              : "bg-bg-elevated text-fg-tertiary hover:text-fg-primary",
                          )}
                        >
                          {tier}
                        </button>
                      ),
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 border-t border-border pt-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      className="flex-1"
                      onClick={() =>
                        setOpenInvoiceFor((cur) =>
                          cur === Number(c.id) ? null : Number(c.id),
                        )
                      }
                    >
                      <Receipt className="h-3 w-3" strokeWidth={1.5} />
                      {openInvoiceFor === Number(c.id)
                        ? t("admin.customers.hide")
                        : t("admin.customers.invoices")}
                    </Button>
                    <Button
                      variant={isHidden ? "secondary" : "ghost"}
                      size="sm"
                      onClick={() => void toggleCustomerVisibility(api)}
                      disabled={busyCustomerIds.has(api.customer_id)}
                      aria-label={
                        isHidden
                          ? t("admin.customers.restoreCustomerAria", {
                              name: c.companyName,
                            })
                          : t("admin.customers.hideCustomerAria", {
                              name: c.companyName,
                            })
                      }
                    >
                      {isHidden ? (
                        <Eye className="h-3 w-3" strokeWidth={1.5} />
                      ) : (
                        <EyeOff className="h-3 w-3" strokeWidth={1.5} />
                      )}
                    </Button>
                  </div>

                  {openInvoiceFor === Number(c.id) && (
                    <CustomerInvoices customerId={Number(c.id)} name={c.companyName} />
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-fg-quaternary">{label}</div>
      <div className="font-510 text-fg-primary">{value}</div>
    </div>
  );
}

// Invoices panel — drilldown to /api/sales/invoices?customer_id=.
// Each row is a button: click to expand the real invoice (header +
// line items, image thumbnails, totals). Reuses the shared
// InvoiceDetailPanel so the data path is identical to /admin/invoices.
function CustomerInvoices({ customerId, name }: { customerId: number; name: string }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<ApiInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openInvoice, setOpenInvoice] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    fetch(`/api/sales/invoices?customer_id=${customerId}&limit=12`, {
      headers: authHeaders(),
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = (await r.json()) as { rows: ApiInvoice[] };
        setRows(body.rows);
      })
      .catch((e) => setError(String(e instanceof Error ? e.message : e)))
      .finally(() => setLoading(false));
  }, [customerId]);

  return (
    <div className="rounded-md border border-border bg-bg-canvas/40 p-3">
      <div className="mb-2 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-fg-tertiary">
        <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
        {t("admin.customers.recentInvoices", { name })}
      </div>
      {error && (
        <p className="text-xs text-accent-rose">{error}</p>
      )}
      {loading ? (
        <p className="text-xs text-fg-tertiary">{t("admin.customers.loading")}</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-fg-tertiary">{t("admin.customers.noInvoices")}</p>
      ) : (
        <div className="space-y-1">
          {rows.map((r) => {
            const isOpen = openInvoice === r.invoice_number;
            return (
              <div key={r.invoice_number} className="rounded">
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={`cust-inv-${customerId}-${r.invoice_number}`}
                  onClick={() => setOpenInvoice((cur) =>
                    cur === r.invoice_number ? null : r.invoice_number)}
                  className="flex w-full items-center gap-2 rounded px-1 py-1 text-left text-xs hover:bg-bg-hover"
                >
                  <ChevronRight
                    className={
                      "h-3 w-3 text-fg-quaternary transition-transform " +
                      (isOpen ? "rotate-90" : "")
                    }
                    strokeWidth={1.5}
                  />
                  <code className="rounded bg-bg-elevated px-1.5 py-0.5 font-mono text-[10px] text-fg-secondary">
                    {r.invoice_number}
                  </code>
                  <span className="text-fg-tertiary">{r.invoice_date ?? "—"}</span>
                  <span className="ml-auto font-mono text-fg-primary">
                    {formatPrice(Number(r.total_tvac ?? 0), "EUR")}
                  </span>
                </button>
                {isOpen && (
                  <div id={`cust-inv-${customerId}-${r.invoice_number}`}>
                    <InvoiceDetailPanel
                      invoiceNumber={r.invoice_number}
                      loadingLabel={t("admin.customers.loadingInvoice", {
                        number: r.invoice_number,
                      })}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-2 flex items-center gap-1 text-[10px] text-fg-quaternary">
        <X className="h-3 w-3" />
        {t("admin.customers.showingRecent", { count: rows.length })}
      </div>
    </div>
  );
}
